import { useState } from "react";
import { FormGenerator, ProofCanvas } from "@proof/ui";
import {
  uid,
  validateSubmission,
  type Project,
  type Values,
} from "@proof/core";
import {
  exportCustomization,
  importCustomization,
  projectToXvp,
  finalizeOrderFile,
  legacyOrderContext,
  type LegacyEnvelope,
} from "@proof/legacy";
import { Button, Control } from "./components";
import { JsonImportButton } from "@proof/authoring/json-import";
import { downloadBlob, downloadJson } from "./api";
import { useHostContext } from "./useHostContext";
import { useWorkspaceTransport } from "./WorkspaceContext";
import { HandoffPackages } from "./HandoffPackages";

/** Local, reviewable exchange data. Persistence and order authorization belong to the host. */
export function OrderHandoff({
  project,
  notify,
}: {
  project: Project;
  notify: (message: string) => void;
}) {
  const transport = useWorkspaceTransport();
  const [values, setValues] = useState<Values>({}),
    [partId, setPart] = useState(""),
    [stateId, setStateId] = useState("1"),
    [comments, setComments] = useState(""),
    [orderNumber, setOrderNumber] = useState("ORDER-1042"),
    [customIdentifier, setCustomIdentifier] = useState("CUSTOM-7"),
    [proofName, setProofName] = useState(project.name),
    [source, setSource] = useState<LegacyEnvelope>({ tables: {} }),
    [page, setPage] = useState(0),
    [showValidation, setShowValidation] = useState(false),
    [warnings, setWarnings] = useState<string[]>([]);
  const host = useHostContext(project, partId, "alex"),
    initialContext = { ...host.context, order: legacyOrderContext() },
    identifiers = {
      ORDER_DISP_NO: orderNumber,
      CUSTOM_IDENTIFIER: customIdentifier,
      PROOF_NAME: proofName,
    },
    finalContext = { ...host.context, order: identifiers },
    key = JSON.stringify([
      project,
      partId,
      values,
      stateId,
      comments,
      identifiers,
      host.key,
      host.hash,
      source,
    ]);
  const [prepared, setPrepared] = useState<{
    key: string;
    payload: {
      version: 1;
      projectId: string;
      stateId: number;
      customization: LegacyEnvelope;
      orderFile: Record<string, unknown>;
      changedVariables: string[];
      unresolved: string[];
    };
  }>();
  const current = prepared?.key === key ? prepared.payload : undefined;
  const prepare = () => {
    try {
      setShowValidation(true);
      if (host.blocked)
        throw new Error(
          "Wait for profile values before preparing the handoff.",
        );
      const checked = validateSubmission(
        project,
        values,
        partId || undefined,
        initialContext,
      );
      if (checked.errors.length)
        throw new Error(
          "Correct the highlighted form values before preparing the handoff.",
        );
      const id = Number(stateId);
      if (!Number.isSafeInteger(id) || id < 1 || id > 2147483647)
        throw new Error("Customization ID must be a positive 32-bit integer.");
      const initialXvp = projectToXvp(
          project,
          checked.snapshot,
          partId || undefined,
          initialContext,
        ),
        original = structuredClone(source);
      const states = (original.tables.ESM_CUSTOMIZATION_STATE ||= []),
        existing = states.find(
          (row) => Number(row.CUSTOMIZATION_STATE_ID) === id,
        );
      if (existing) existing.PAGEFLEX_XVP_XML = initialXvp;
      else
        states.push({
          CUSTOMIZATION_STATE_ID: id,
          PAGEFLEX_XVP_XML: initialXvp,
        });
      const customization = exportCustomization(
        project,
        {
          id: uid("proof"),
          tenantId: project.tenantId,
          projectId: project.id,
          partId: partId || undefined,
          name: proofName,
          values: checked.snapshot,
          comments,
          accepted: false,
          initials: "",
          at: new Date().toISOString(),
        },
        { mode: "portable", stateId: id },
        original,
      );
      const finalized = finalizeOrderFile(
        {
          FILES_ORDERS_ID: 1,
          PAGEFLEX_XVP_XML: initialXvp,
          SP_LOW_RES_PDF: "demo-proof.pdf",
          HIGH_RES_PDF: "",
        },
        identifiers,
      );
      setPrepared({
        key,
        payload: {
          version: 1,
          projectId: project.id,
          stateId: id,
          customization,
          orderFile: finalized.row,
          changedVariables: finalized.changed,
          unresolved: finalized.unresolved,
        },
      });
      notify("Order handoff prepared for review and download.");
    } catch (error) {
      setPrepared(undefined);
      notify((error as Error).message);
    }
  };
  const snapshot = () => ({
    values,
    partId,
    comments,
    stateId,
    proofName,
    source,
    warnings,
  });
  const apply = (state: ReturnType<typeof snapshot>) => {
    setValues(state.values);
    setPart(state.partId);
    setComments(state.comments);
    setStateId(state.stateId);
    setProofName(state.proofName);
    setSource(state.source);
    setWarnings(state.warnings);
    setPrepared(undefined);
    setPage(0);
  };
  const [beforeRestore, setBeforeRestore] =
    useState<ReturnType<typeof snapshot>>();
  const inspectCustomization = (raw: unknown) => {
    if (!raw || typeof raw !== "object" || Array.isArray(raw))
      throw new Error(
        "This file isn't a saved customization. Use a file from “Download handoff JSON”.",
      );
    const data = raw as {
        projectId?: string;
        stateId?: string | number;
        customization?: LegacyEnvelope;
      },
      envelope = (data.customization || data) as LegacyEnvelope;
    if (data.projectId && data.projectId !== project.id)
      throw new Error("CUSTOMIZATION_SCOPE_MISMATCH");
    const restored = importCustomization(project, envelope, data.stateId as never);
    const filled = Object.values(restored.proof.values).filter(
      (value) => value !== "" && value !== undefined && value !== null,
    ).length;
    const part = project.parts.find((item) => item.id === restored.proof.partId);
    return {
      restored,
      plan: {
        title: restored.proof.name || "Saved customization",
        summary: [
          { label: "Customization ID", value: String(restored.ids.stateId) },
          { label: "Part", value: part?.name ?? "Main project" },
          { label: "Filled-in values", value: String(filled) },
          {
            label: "Comments",
            value: restored.proof.comments ? "Included" : "None",
          },
        ],
        before: values,
        after: restored.proof.values,
        warnings: [
          ...(Object.keys(values).length
            ? [
                "Restoring replaces the values currently in this form. You can undo it afterwards.",
              ]
            : []),
          ...restored.warnings,
        ],
        confirmLabel: "Restore customization",
      },
    };
  };
  return (
    <>
      <HandoffPackages projectId={project.id} notify={notify} />
      <div className="integration-detail-grid">
        <section className="panel padded">
          <h2>Order handoff</h2>
          <p>
            Save and restore customization values, then replace order
            placeholders. Downloads use portable demo IDs; the host supplies
            database identities and writes the reviewed data.
          </p>
          <Control label="Handoff part">
            <select
              value={partId}
              onChange={(e) => {
                setPart(e.target.value);
                setPage(0);
              }}
            >
              <option value="">Main project</option>
              {project.parts.map((part) => (
                <option key={part.id} value={part.id}>
                  {part.name}
                </option>
              ))}
            </select>
          </Control>
          <Control label="Customization ID">
            <input
              inputMode="numeric"
              value={stateId}
              onChange={(e) => setStateId(e.target.value)}
            />
          </Control>
          <Control label="Order display number">
            <input
              value={orderNumber}
              onChange={(e) => setOrderNumber(e.target.value)}
            />
          </Control>
          <Control label="Custom identifier">
            <input
              value={customIdentifier}
              onChange={(e) => setCustomIdentifier(e.target.value)}
            />
          </Control>
          <Control label="Order proof name">
            <input
              value={proofName}
              onChange={(e) => setProofName(e.target.value)}
            />
          </Control>
          <Control label="Handoff comments">
            <textarea
              value={comments}
              onChange={(e) => setComments(e.target.value)}
            />
          </Control>
          {host.blocked && (
            <p role={host.current?.error ? "alert" : "status"}>
              {host.current?.error || "Loading profile values…"}
            </p>
          )}
          <FormGenerator
            loadAsset={transport.loadAsset}
            transformImage={transport.proofServices.transformImage}
            project={project}
            values={values}
            onChange={setValues}
            partId={partId || undefined}
            context={initialContext}
            compact
            showValidation={showValidation}
          />
          <div className="integration-actions">
            <Button kind="primary" disabled={host.blocked} onClick={prepare}>
              Prepare order handoff
            </Button>
            <JsonImportButton
              label="Restore customization JSON"
              title="Restore a saved customization"
              description={
                <p>
                  Use a file saved with “Download handoff JSON” from this
                  project. You'll see what changes before anything is replaced.
                </p>
              }
              maxBytes={10_000_000}
              inspect={(raw) => inspectCustomization(raw).plan}
              onConfirm={(raw) => {
                const { restored } = inspectCustomization(raw);
                setBeforeRestore(snapshot());
                apply({
                  values: restored.proof.values,
                  partId: restored.proof.partId || "",
                  comments: restored.proof.comments,
                  stateId: String(restored.ids.stateId),
                  proofName: restored.proof.name,
                  source: restored.envelope,
                  warnings: restored.warnings,
                });
                notify(
                  "Customization restored. Review current profile values before preparing a new handoff.",
                );
                return "Customization restored. Review the profile values before preparing a new handoff.";
              }}
              onUndo={() => {
                if (beforeRestore) apply(beforeRestore);
                notify("Restore undone. The previous values are back.");
              }}
            />
          </div>
          {warnings.length > 0 && (
            <ul>
              {warnings.map((warning, i) => (
                <li key={i}>{warning}</li>
              ))}
            </ul>
          )}
        </section>
        <section className="panel padded">
          <h2>Final order preview</h2>
          <p>
            This local preview uses the entered identifiers. Server generation
            still uses the authenticated host context.
          </p>
          <Control label="Handoff preview page">
            <select
              value={page}
              onChange={(e) => setPage(Number(e.target.value))}
            >
              {project.pages.map((item, index) => (
                <option key={item.id} value={index}>
                  {item.name}
                </option>
              ))}
            </select>
          </Control>
          <ProofCanvas
            {...transport.proofServices}
            project={project}
            values={values}
            context={finalContext}
            partId={partId || undefined}
            pageIndex={page}
            zoom={0.65}
          />
          {current && (
            <>
              <p role="status">
                Handoff prepared. {current.changedVariables.length} order
                variables updated. {current.unresolved.length} unresolved
                placeholders.
              </p>
              <p>
                {current.orderFile.SP_LOW_RES_PDF === ""
                  ? "Cached low-resolution proof cleared after the order values changed."
                  : "Cached proof reference retained because no order values changed."}
              </p>
              <Control label="Final order XVP">
                <textarea
                  readOnly
                  rows={8}
                  value={String(current.orderFile.PAGEFLEX_XVP_XML)}
                />
              </Control>
              <div className="integration-actions">
                <Button
                  onClick={() =>
                    downloadJson(current, `${project.id}-order-handoff.json`)
                  }
                >
                  Download handoff JSON
                </Button>
                <Button
                  onClick={() =>
                    downloadBlob(
                      new Blob([String(current.orderFile.PAGEFLEX_XVP_XML)], {
                        type: "application/xml",
                      }),
                      `${project.id}.xvp`,
                    )
                  }
                >
                  Download final XVP
                </Button>
              </div>
            </>
          )}
        </section>
      </div>
    </>
  );
}
